import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, isRef as _isRef, withCtx as _withCtx, createBlock as _createBlock, Teleport as _Teleport } from "vue"

const _hoisted_1 = { class: "col-12" }
const _hoisted_2 = {
  key: 0,
  class: "text-center py-4"
}
const _hoisted_3 = { class: "mt-2" }
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { class: "mb-3" }
const _hoisted_6 = { class: "language-list" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "language-title" }
const _hoisted_9 = { class: "language-flags" }
const _hoisted_10 = ["onClick"]
const _hoisted_11 = ["src", "alt"]
const _hoisted_12 = {
  key: 1,
  class: "international-locale"
}

import { ref, computed, watch, getCurrentInstance, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useLanguageModal } from '@/composables/useLanguageModal';
import BaseModal from '@/composables/BaseModal.vue';
import { Language } from '@/types/Language';
import { RecommendedLanguage } from '@/store/modules/recommendedLanguages';
import { latestMessage } from '@/plugins/ws';
import {useTranslation} from "@/composables/useTranslation";

// Используем composable для управления модальным окном

export default /*@__PURE__*/_defineComponent({
  __name: 'LanguageModal',
  setup(__props) {

const { isOpen, closeModal } = useLanguageModal();
const { tg } = useTranslation();

const store = useStore();
const internalInstance = getCurrentInstance();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const wsSend = internalInstance?.appContext.config.globalProperties.$wsSend as (signal: string, payload?: any) => void;

// Переменные для управления модальным окном всех языков
const languageSwitcherReady = ref(false);
const languageSwitcherSubscribed = ref(false);

// Computed свойства
const languages = computed<Language[]>(() => store.state.languages.languages);
const recommendedLanguages = computed(() => {
  const languages = store.getters['recommendedLanguages/getUniqueLanguages'];
  return languages.sort((a: RecommendedLanguage, b: RecommendedLanguage) => a.weight < b.weight ? 1 : (a.weight > b.weight ? -1 : 0));
});

const idLanguage = computed(() => store.state.i18n.idLanguage);
const idLocale = computed(() => store.state.i18n.idLocale);

const allLanguages = computed(() =>
    languages.value
        .filter(language => language.locked)
        .sort((a, b) => a.code.localeCompare(b.code))
);

// Получаем данные из store
const locales = computed(() => store.state.locales.locales);
const countries = computed(() => store.state.countries.countries);

// Функции для работы с локалями и странами
function getLocalesForLanguage(idLanguage: number) {
  return locales.value.filter(locale => locale.idLanguage === idLanguage);
}

function getCountryForLocale(idLocale: number) {
  const locale = locales.value.find(l => l.idLocale === idLocale);
  if (!locale || !locale.idCountry) return null;
  return countries.value.find(c => c.idCountry === locale.idCountry);
}

// Функции для работы с рекомендованными языками
function isRecommendedLanguage(idLanguage: number) {
  return recommendedLanguages.value.some((lang: RecommendedLanguage) => lang.idLanguage === idLanguage);
}

function isRecommendedLocale(idLanguage: number, idLocale: number) {
  return recommendedLanguages.value.some((lang: RecommendedLanguage) =>
      lang.idLanguage === idLanguage && lang.idLocale === idLocale
  );
}

function isSelectedLocale(languageId: number, localeId: number) {
  return idLanguage.value === languageId && idLocale.value === localeId;
}

function handleLanguageClick(language: Language) {
  const locales = getLocalesForLanguage(language.idLanguage);

  // Если одна или ноль локалей - кликаем на весь язык
  if (locales.length <= 1) {
    const firstLocale = locales[0];
    selectLanguage(language.idLanguage, firstLocale?.idLocale || null);
  } else {
    // Если больше одной локали - выбираем первую рекомендованную или первую доступную
    const recommendedLocale = locales.find(locale =>
        isRecommendedLocale(language.idLanguage, locale.idLocale)
    );
    const localeToSelect = recommendedLocale || locales[0];
    selectLanguage(language.idLanguage, localeToSelect.idLocale);
  }
}

function selectLanguage(idLanguage: number | null, idLocale: number | null) {
  // Если idLanguage null - это автовыбор, передаем null значения
  if (idLanguage === null) {
    wsSend('profileUpdateLocale', {
      idLanguage: null,
      idLocale: null,
    });
    // НЕ закрываем модалку при автовыборе - пользователь может продолжить выбор
    return;
  }

  // В новой структуре данных idLocale уже приходит с языком
  // Если idLocale все еще null, найдем его в recommendedLanguages
  if (idLocale === null) {
    const recommendedLanguage = recommendedLanguages.value.find((lang: RecommendedLanguage) => lang.idLanguage === idLanguage);
    if (recommendedLanguage) {
      idLocale = recommendedLanguage.idLocale;
    }
  }

  wsSend('profileUpdateLocale', {
    idLanguage,
    idLocale,
  });
  
  // НЕ закрываем модалку при выборе языка - пользователь может выбрать несколько
}

// Lifecycle и watchers
onMounted(() => {
  // Модальное окно открывается через composable, не нужно ничего делать здесь
});

onBeforeUnmount(() => {
  if (languageSwitcherSubscribed.value) {
    wsSend('unsubscribeGroup', {
      group: 'languageSwitcher',
    });
  }
});

// Отслеживаем открытие модального окна
watch(isOpen, (newValue) => {
  if (newValue) {
    openLanguageModal();
  } else {
    // Модальное окно закрывается - ничего не делаем, так как closeLanguageModal уже вызвана
  }
});

function openLanguageModal() {
  languageSwitcherReady.value = false;
  languageSwitcherSubscribed.value = true;
  wsSend('subscribeGroup', {
    group: 'languageSwitcher',
  });
}

function closeLanguageModal() {
  if (languageSwitcherSubscribed.value) {
    wsSend('unsubscribeGroup', {
      group: 'languageSwitcher',
    });
    languageSwitcherSubscribed.value = false;
    languageSwitcherReady.value = false;
  }
  closeModal();
}

function handleCancel() {
  closeLanguageModal();
}

// Обработка WebSocket сообщений
watch(latestMessage, (data) => {
  if (!data || !data.signal) return;
  switch (data.signal) {
    case 'language-switcher-data':
      signalLanguageSwitcherData();
      break;
  }
});

function signalLanguageSwitcherData() {
  languageSwitcherReady.value = true;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    (_unref(isOpen))
      ? (_openBlock(), _createBlock(BaseModal, {
          key: 0,
          modelValue: _unref(isOpen),
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(isOpen) ? (isOpen).value = $event : null)),
          title: _unref(tg)('languageSwitcher', 'allLanguages'),
          modalName: "language-switcher",
          onCancel: handleCancel
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_1, [
              (!languageSwitcherReady.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                    _cache[2] || (_cache[2] = _createElementVNode("div", {
                      class: "spinner-border",
                      role: "status"
                    }, [
                      _createElementVNode("span", { class: "visually-hidden" }, "Loading...")
                    ], -1)),
                    _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(tg)('languageSwitcher', 'loading')), 1)
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_4, [
                    _createElementVNode("div", _hoisted_5, [
                      _createElementVNode("button", {
                        class: _normalizeClass(["btn w-100", idLanguage.value === null ? 'btn-primary' : 'btn-outline-secondary']),
                        onClick: _cache[0] || (_cache[0] = ($event: any) => (selectLanguage(null, null)))
                      }, [
                        _cache[3] || (_cache[3] = _createElementVNode("i", { class: "bi bi-arrow-clockwise me-2" }, null, -1)),
                        _createTextVNode(" " + _toDisplayString(_unref(tg)('languageSwitcher', 'autoSelect')), 1)
                      ], 2)
                    ]),
                    _createElementVNode("div", _hoisted_6, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(allLanguages.value, (language) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: language.idLanguage,
                          class: _normalizeClass(["language-row", {
                    'active': language.idLanguage === idLanguage.value,
                    'recommended': isRecommendedLanguage(language.idLanguage),
                    'rtl': language.rtl
                  }]),
                          onClick: ($event: any) => (handleLanguageClick(language)),
                          style: {"cursor":"pointer"}
                        }, [
                          _createElementVNode("div", _hoisted_8, _toDisplayString(language.selfTitle), 1),
                          _createElementVNode("div", _hoisted_9, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getLocalesForLanguage(language.idLanguage), (locale) => {
                              return (_openBlock(), _createElementBlock("div", {
                                key: locale.idLocale,
                                class: _normalizeClass(["flag-item", {
                          'recommended': isRecommendedLocale(language.idLanguage, locale.idLocale),
                          'selected': isSelectedLocale(language.idLanguage, locale.idLocale)
                        }]),
                                onClick: _withModifiers(($event: any) => (selectLanguage(language.idLanguage, locale.idLocale)), ["stop"])
                              }, [
                                (getCountryForLocale(locale.idLocale)?.code)
                                  ? (_openBlock(), _createElementBlock("img", {
                                      key: 0,
                                      src: '/images/country/' + getCountryForLocale(locale.idLocale)?.code + '/20x16.png',
                                      alt: getCountryForLocale(locale.idLocale)?.code + ' flag',
                                      class: "flag-image"
                                    }, null, 8, _hoisted_11))
                                  : (_openBlock(), _createElementBlock("div", _hoisted_12, _cache[4] || (_cache[4] = [
                                      _createElementVNode("i", { class: "bi bi-globe" }, null, -1)
                                    ])))
                              ], 10, _hoisted_10))
                            }), 128))
                          ])
                        ], 10, _hoisted_7))
                      }), 128))
                    ])
                  ]))
            ])
          ]),
          actions: _withCtx(() => [
            _createElementVNode("button", {
              class: "btn btn-secondary",
              onClick: closeLanguageModal
            }, _toDisplayString(_unref(tg)('languageSwitcher', 'modalClose')), 1)
          ]),
          _: 1
        }, 8, ["modelValue", "title"]))
      : _createCommentVNode("", true)
  ]))
}
}

})